import React, { useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Session, WorkshopDayScheduleRow } from './sessionTypes';
import {
  formatSessionStartLabel,
  formatWorkshopDayStartLabel,
  getSessionDeliveryTypeLabel,
  sessionNameSubtitle,
  isOptionalSession,
  isPreviousWorkshopDayCompleted,
  isWorkshopSession,
  resolveWorkshopDays,
  sessionTablePaperSx,
  sessionTableContainerSx,
} from './sessionUtils';
import { LiveKitPodStatusDot } from './LiveKitPodStatusDot';
import { SessionIdBadge } from './SessionIdBadge';
import EditSessionModal from './EditSessionModal';
import ConfirmationModal from '../../../../components/ConfirmationModal';

interface OngoingSessionsProps {
  sessions: Session[];
  loadingSessions: Set<number>;
  loadingStarts?: Set<number>;
  batchLabel?: string;
  onEndSession: (session: Session) => void;
  onDeleteSession: (session: Session) => void;
  onStartSession: (session: Session, dayIndex?: number) => void;
  onJoinSession?: (session: Session, dayIndex?: number) => void;
  onShowReport?: (session: Session) => void;
  onManageStudents?: (session: Session) => void;
  onSessionsChange?: (sessions: Session[]) => void;
  /** Refresh list after edit (e.g. expected resume). */
  onSessionUpdated?: () => void;
  pagination: { pageIndex: number; pageSize: number };
  rowCount: number;
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
}

const ACTION_ICON_SLOT = {
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

function workshopDays(session: Session): WorkshopDayScheduleRow[] {
  return resolveWorkshopDays(session);
}

function topicsForDay(session: Session, day: WorkshopDayScheduleRow): string[] {
  if (Array.isArray(day.topics) && day.topics.length) return day.topics;
  const plan = session.topic_plan || [];
  return plan
    .filter((t) => {
      const idx = t.workshop_day_index ?? t.workshopDayIndex;
      return Number(idx ?? 0) === Number(day.day_index);
    })
    .map((t) => String(t.title || t.name || '').trim())
    .filter(Boolean);
}

const OngoingSessions: React.FC<OngoingSessionsProps> = ({
  sessions,
  loadingSessions,
  loadingStarts,
  batchLabel = '',
  onEndSession,
  onDeleteSession,
  onStartSession,
  onJoinSession,
  onShowReport,
  onManageStudents,
  onSessionsChange,
  onSessionUpdated,
  pagination,
  rowCount,
  onPaginationChange,
}) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [cancelTarget, setCancelTarget] = useState<Session | null>(null);
  const [editTarget, setEditTarget] = useState<Session | null>(null);

  useEffect(() => {
    if (!onSessionsChange) return;
    const selectedSessions = sessions.filter((s) => rowSelection[s.id.toString()]);
    onSessionsChange(selectedSessions);
  }, [rowSelection, sessions, onSessionsChange]);

  const renderDayPrimaryButton = (session: Session, day: WorkshopDayScheduleRow) => {
    const joining = loadingStarts?.has(session.id);
    const days = workshopDays(session);
    const hasLiveDay = days.some((d) => d.can_join || d.status === 'live');
    const isThisDayLive = day.can_join || day.status === 'live';
    const prevCompleted = isPreviousWorkshopDayCompleted(days, Number(day.day_index));
    const canStartDay =
      day.can_start !== false &&
      prevCompleted &&
      !hasLiveDay &&
      !joining;

    if (day.has_ended || day.status === 'ended' || day.status === 'completed') {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onShowReport?.(session);
          }}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            minWidth: 84,
            fontWeight: 700,
            color: '#2563eb',
            borderColor: '#bfdbfe',
            '&:hover': { backgroundColor: '#eff6ff', borderColor: '#93c5fd' },
          }}
        >
          Report
        </Button>
      );
    }
    if (isThisDayLive) {
      return (
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onJoinSession?.(session, day.day_index);
          }}
          sx={{
            textTransform: 'none',
            bgcolor: '#16a34a',
            fontWeight: 600,
            borderRadius: '8px',
            px: 1.5,
            minWidth: 84,
            '&:hover': { bgcolor: '#15803d' },
          }}
        >
          Join
        </Button>
      );
    }
    return (
      <Tooltip
        title={
          prevCompleted
            ? hasLiveDay
              ? 'End the live day first'
              : 'Start this day'
            : 'Complete the previous day first'
        }
      >
        <span>
          <Button
            variant="contained"
            size="small"
            startIcon={joining ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onStartSession(session, day.day_index);
            }}
            disabled={!canStartDay}
            sx={{
              textTransform: 'none',
              bgcolor: '#16a34a',
              fontWeight: 600,
              borderRadius: '8px',
              px: 1.5,
              minWidth: 84,
              '&:hover': { bgcolor: '#15803d' },
            }}
          >
            Start
          </Button>
        </span>
      </Tooltip>
    );
  };

  const renderDayEndButton = (session: Session, day: WorkshopDayScheduleRow) => {
    const ending = loadingSessions.has(session.id);
    const isThisDayLive = day.can_join || day.status === 'live';
    return (
      <Tooltip title={isThisDayLive ? 'End this day' : 'End day (start day first)'}>
        <span>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onEndSession(session);
            }}
            disabled={ending || !isThisDayLive}
            aria-label="End day"
            sx={{
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              color: '#dc2626',
              backgroundColor: '#fff',
              width: 32,
              height: 32,
              '&:hover': {
                borderColor: '#f87171',
                backgroundColor: '#fef2f2',
              },
              '&.Mui-disabled': {
                borderColor: '#e2e8f0',
                color: '#94a3b8',
              },
            }}
          >
            {ending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CancelOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const columns = [
    {
      accessorKey: 'Session_Topic',
      header: 'SESSION NAME',
      Cell: ({ row }: { row: { original: Session; getIsExpanded: () => boolean } }) => {
        const session = row.original;
        const subtitle = sessionNameSubtitle(session, batchLabel);
        const days = workshopDays(session);
        const isWorkshop = isWorkshopSession(session) && days.length > 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            {isWorkshop ? (
              <Box
                sx={{
                  color: '#64748b',
                  mt: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {row.getIsExpanded() ? (
                  <KeyboardArrowDownIcon fontSize="small" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" />
                )}
              </Box>
            ) : null}
            <Box>
              <Typography
                sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14, lineHeight: 1.3 }}
              >
                {session.Session_Topic}
              </Typography>
              <SessionIdBadge sessionId={session.id} />
              {subtitle ? (
                <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.25 }}>
                  {subtitle}
                </Typography>
              ) : null}
              {String(session.session_status || '')
                .trim()
                .toLowerCase() === 'paused' ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    size="small"
                    label="Paused"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: '#ffedd5',
                      color: '#9a3412',
                    }}
                  />
                  <Chip
                    size="small"
                    label="Incomplete"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: '#e2e8f0',
                      color: '#475569',
                    }}
                  />
                  {session.expected_resume_at ? (
                    <Typography sx={{ color: '#64748b', fontSize: 11 }}>
                      Expected resume:{' '}
                      {(() => {
                        try {
                          return new Date(session.expected_resume_at).toLocaleString();
                        } catch {
                          return String(session.expected_resume_at);
                        }
                      })()}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
              {isWorkshop ? (
                <Typography sx={{ color: '#94a3b8', fontSize: 11, mt: 0.25 }}>
                  {days.length} day{days.length === 1 ? '' : 's'} · click row to expand
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      },
    },
    {
      accessorKey: 'conductedby',
      header: 'TRAINER',
      Cell: ({ row }: { row: { original: Session } }) => {
        const name = row.original.conductedby || '—';
        const initial = name.trim().charAt(0).toUpperCase() || '?';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#e2e8f0', color: '#334155' }}
            >
              {initial}
            </Avatar>
            <Typography sx={{ fontSize: 14, color: '#0f172a' }}>{name}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'type',
      header: 'TYPE',
      size: 100,
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155' }}>
          {getSessionDeliveryTypeLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'start',
      header: 'START',
      size: 170,
      Cell: ({ row }: { row: { original: Session } }) => {
        const session = row.original;
        const days = workshopDays(session);
        if (isWorkshopSession(session) && days.length > 0) {
          // Parent row: single start line (day times live in expanded rows)
          return (
            <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
              {formatWorkshopDayStartLabel({
                start_date: days[0].start_date,
                start_time: days[0].start_time,
              })}
            </Typography>
          );
        }
        return (
          <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
            {formatSessionStartLabel(session)}
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      size: 180,
      minSize: 160,
      enableResizing: false,
      Cell: ({ row }: { row: { original: Session } }) => {
        const session = row.original;
        const isSessionStarted =
          !!session.Actual_Start_Time_time && session.Actual_Start_Time_time !== '';
        const hasEnded = !!session.End_Time_time && session.End_Time_time !== '';
        const isPaused =
          String(session.session_status || '').trim().toLowerCase() === 'paused';
        const isLiveKit = (session.session_provider || '').toLowerCase() === 'livekit';
        const meetHref = session.meetlink || undefined;
        const joining = loadingStarts?.has(session.id);
        const ending = loadingSessions.has(session.id);
        const showManageStudents =
          isOptionalSession(session) && Boolean(onManageStudents);
        const days = workshopDays(session);
        const isWorkshop = isWorkshopSession(session) && days.length > 0;

        if (isWorkshop) {
          return (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '14px 36px auto 36px',
                gap: 0.75,
                alignItems: 'center',
                width: 'fit-content',
                minWidth: 182,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{
                  width: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LiveKitPodStatusDot session={session} />
              </Box>
              <Box sx={ACTION_ICON_SLOT}>
                {showManageStudents ? (
                  <Tooltip title="Manage students">
                    <IconButton
                      size="small"
                      onClick={() => onManageStudents?.(session)}
                      sx={{
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        width: 32,
                        height: 32,
                      }}
                    >
                      <PersonAddOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
              <Box sx={{ minWidth: 84 }} />
              <Box sx={{ width: 32, height: 32 }} />
            </Box>
          );
        }

        const joinButton = (() => {
          if (isPaused) {
            return (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  joining ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
                }
                onClick={() => onStartSession(session)}
                disabled={joining}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#d97706',
                  fontWeight: 600,
                  borderRadius: '8px',
                  minWidth: 84,
                  '&:hover': { bgcolor: '#b45309' },
                }}
              >
                Resume
              </Button>
            );
          }
          if (!isSessionStarted) {
            return (
              <Button
                variant="contained"
                size="small"
                startIcon={
                  joining ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
                }
                onClick={() => onStartSession(session)}
                disabled={joining}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#16a34a',
                  fontWeight: 600,
                  borderRadius: '8px',
                  minWidth: 84,
                  '&:hover': { bgcolor: '#15803d' },
                }}
              >
                Start
              </Button>
            );
          }
          if (hasEnded) {
            return (
              <Button variant="contained" size="small" disabled sx={{ minWidth: 84 }}>
                Join
              </Button>
            );
          }
          const onJoin = () => onJoinSession?.(session);
          return (
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon />}
              {...(isLiveKit || !meetHref
                ? { onClick: onJoin }
                : {
                    component: 'a' as const,
                    href: meetHref,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    onClick: onJoin,
                  })}
              sx={{
                textTransform: 'none',
                bgcolor: '#16a34a',
                fontWeight: 600,
                borderRadius: '8px',
                minWidth: 84,
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              Join
            </Button>
          );
        })();

        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '14px 36px auto 36px',
              gap: 0.75,
              alignItems: 'center',
              width: 'fit-content',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                width: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LiveKitPodStatusDot session={session} />
            </Box>
            <Box sx={ACTION_ICON_SLOT}>
              {isPaused ? (
                <Tooltip title="Edit session">
                  <IconButton
                    size="small"
                    onClick={() => setEditTarget(session)}
                    sx={{
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      width: 32,
                      height: 32,
                      color: '#2563eb',
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : showManageStudents ? (
                <Tooltip title="Manage students">
                  <IconButton
                    size="small"
                    onClick={() => onManageStudents?.(session)}
                    sx={{
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      width: 32,
                      height: 32,
                    }}
                  >
                    <PersonAddOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
            {joinButton}
            <Tooltip
              title={
                isPaused || isSessionStarted
                  ? 'End session'
                  : isOptionalSession(session)
                    ? 'Cancel optional session'
                    : 'Cancel session'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    if (isPaused || isSessionStarted) {
                      onEndSession(session);
                      return;
                    }
                    // Not started yet — cancel/delete (same as Upcoming).
                    // Optional sessions often land in Ongoing before Start.
                    setCancelTarget(session);
                  }}
                  disabled={ending}
                  sx={{
                    border: '1px solid #fca5a5',
                    borderRadius: '8px',
                    width: 32,
                    height: 32,
                    '&.Mui-disabled': {
                      borderColor: '#e2e8f0',
                      color: '#94a3b8',
                    },
                  }}
                >
                  {ending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CancelOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <MaterialReactTable
        columns={columns}
        data={sessions}
        enableColumnFilters={false}
        enableRowSelection={false}
        enableExpanding
        getRowCanExpand={(row) => {
          const s = row.original;
          return isWorkshopSession(s) && workshopDays(s).length > 0;
        }}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            size: 36,
            minSize: 36,
            maxSize: 36,
            header: '',
            enableResizing: false,
            Cell: () => null,
            muiTableHeadCellProps: {
              sx: { width: 36, maxWidth: 36, p: 0, borderBottom: '1px solid #e5e7eb' },
            },
            muiTableBodyCellProps: {
              sx: { width: 36, maxWidth: 36, p: 0 },
            },
          },
        }}
        muiExpandButtonProps={{ sx: { display: 'none' } }}
        state={{ pagination, expanded }}
        onExpandedChange={(updater: any) => {
          setExpanded((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (typeof next === 'boolean') return next ? prev : {};
            return (next || {}) as Record<string, boolean>;
          });
        }}
        manualPagination
        rowCount={rowCount}
        onPaginationChange={(updater: any) => {
          const next = typeof updater === 'function' ? updater(pagination) : updater;
          onPaginationChange(next);
        }}
        getRowId={(row) => row.id.toString()}
        renderDetailPanel={({ row }) => {
          const session = row.original;
          const days = workshopDays(session);
          if (!days.length) return null;
          // Mirror parent data columns (Session | Trainer | Type | Start | Actions).
          // Offset past MRT expand column so cells line up with headers.
          const dayRowSx = {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 2.2fr) minmax(0, 1.5fr) 100px minmax(0, 1.2fr) 180px',
            columnGap: 0,
            alignItems: 'center',
            width: 'calc(100% - 36px)',
            ml: '36px',
            px: 2,
            py: 1.25,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            boxSizing: 'border-box',
            '&:last-of-type': { borderBottom: 'none' },
          } as const;
          return (
            <Box sx={{ width: '100%', bgcolor: '#f8fafc' }}>
              {days.map((day) => {
                const topics = topicsForDay(session, day);
                return (
                  <Box key={day.day_index} sx={dayRowSx}>
                    <Box sx={{ pr: 1.5, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                        {day.day_label || `Day ${day.day_index + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>
                        {day.status || 'scheduled'}
                        {topics.length ? ` · ${topics.join(', ')}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1.5 }}>
                      <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>—</Typography>
                    </Box>
                    <Box sx={{ pr: 1.5 }}>
                      <Typography sx={{ fontSize: 14, color: '#334155' }}>
                        {getSessionDeliveryTypeLabel(session)}
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1.5, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
                        {formatWorkshopDayStartLabel({
                          start_date: day.start_date,
                          start_time: day.start_time,
                        })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '36px auto 36px',
                        gap: 0.75,
                        alignItems: 'center',
                        width: 'fit-content',
                        minWidth: 168,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box sx={ACTION_ICON_SLOT} />
                      {renderDayPrimaryButton(session, day)}
                      {renderDayEndButton(session, day)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        }}
        muiDetailPanelProps={{
          sx: {
            bgcolor: '#f8fafc',
            '& .MuiCollapse-wrapperInner': { width: '100%' },
          },
        }}
        muiTableBodyRowProps={({ row, isDetailPanel }) => {
          if (isDetailPanel) {
            return {
              sx: {
                bgcolor: '#f8fafc',
                '& > td': {
                  p: 0,
                  borderBottom: '1px solid #e2e8f0',
                },
              },
            };
          }
          const canExpand = row.getCanExpand();
          return {
            onClick: () => {
              if (!canExpand) return;
              row.toggleExpanded();
            },
            sx: {
              cursor: canExpand ? 'pointer' : 'default',
              '&:hover': canExpand ? { bgcolor: '#f8fafc' } : undefined,
            },
          };
        }}
        muiTablePaperProps={{ sx: { ...sessionTablePaperSx } }}
        enableTopToolbar={false}
        muiTableHeadCellProps={{
          sx: {
            color: '#94a3b8',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: '1px solid #f1f5f9',
            py: 1.5,
          },
        }}
        muiTableContainerProps={{ sx: { ...sessionTableContainerSx } }}
        initialState={{ density: 'comfortable' }}
      />

      <ConfirmationModal
        show={cancelTarget != null}
        onHide={() => setCancelTarget(null)}
        onConfirm={() => {
          if (cancelTarget) onDeleteSession(cancelTarget);
          setCancelTarget(null);
        }}
        title="Cancel session"
        message={
          cancelTarget
            ? `Cancel "${cancelTarget.Session_Topic}"? It will be removed from ongoing sessions.`
            : ''
        }
        confirmText="Cancel session"
        cancelText="Keep session"
        variant="danger"
      />

      {editTarget ? (
        <EditSessionModal
          open
          session={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdate={() => {
            setEditTarget(null);
            onSessionUpdated?.();
          }}
        />
      ) : null}
    </Box>
  );
};

export default OngoingSessions;
